import React, { useEffect, useRef, useState, useCallback } from 'react';
import { HeatmapFilters, HeatmapFeatureCollection, Viewport } from './types/HeatmapTypes';
import { getColorForIccid, formatBytes } from './utils/colors';
import {
    SIMLY_PARTNER_API_URL,
    getAccessToken,
    SIMLY_HEATMAP_API_URL,
    CAMPAIGN_ID,
    GOOGLE_MAPS_API_KEY
} from '../../../utils/config';

interface Props {
  filters: HeatmapFilters;
}

declare global {
  interface Window {
    google: any;
  }
}

const HeatmapView: React.FC<Props> = ({ filters }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<HeatmapFeatureCollection['metadata'] | null>(null);
  const circlesRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);

  // Initialize Google Maps
  useEffect(() => {
    if (!mapRef.current || map) return;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
    script.async = true;
    script.onload = () => {
      const newMap = new window.google.maps.Map(mapRef.current!, {
        center: { lat: 51.5074, lng: -0.1278 }, // London
        zoom: 6,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
          {
            featureType: 'transit',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
        mapTypeControl: true,
        mapTypeControlOptions: {
          position: window.google.maps.ControlPosition.TOP_RIGHT,
        },
        zoomControl: true,
        streetViewControl: false,
        fullscreenControl: true,
      });

      // Create a single info window to reuse
      infoWindowRef.current = new window.google.maps.InfoWindow();

      setMap(newMap);
    };
    document.head.appendChild(script);
  }, [map]);

  const fetchHeatmap = useCallback(async (viewport: Viewport) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${SIMLY_HEATMAP_API_URL}/campaign/${CAMPAIGN_ID}/viewport`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`
        },
        body: JSON.stringify({
          ...filters,
          viewport,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch heatmap' }));
        throw new Error(errorData.error || 'Failed to fetch heatmap');
      }

      const data: HeatmapFeatureCollection = await response.json();
      setMetadata(data.metadata);

      // Clear existing circles
      circlesRef.current.forEach(c => c.setMap(null));
      circlesRef.current = [];

      // Get current zoom level for dynamic sizing
      const zoom = map?.getZoom() || 6;
      const zoomFactor = Math.pow(2, 14 - zoom); // Inverse zoom scaling

      // Draw new circles
      data.features.forEach(feature => {
        const [lon, lat] = feature.geometry.coordinates;
        const { iccid, count, bytesUsed, lastSeen, firstSeen } = feature.properties;

        const color = getColorForIccid(iccid);
        // Scale radius based on both count and zoom level
        const baseRadius = Math.sqrt(count) * 100; // Increased from 50 to 100
        const radius = baseRadius * zoomFactor;
        const opacity = 0.4 + Math.min(count / 100, 0.4);

        const circle = new window.google.maps.Circle({
          map,
          center: { lat, lng: lon },
          radius,
          fillColor: color,
          fillOpacity: opacity,
          strokeColor: color,
          strokeWeight: 2,
          strokeOpacity: 0.8,
          clickable: true,
        });

        circle.addListener('click', () => {
          if (!infoWindowRef.current) return;

          const content = `
            <div style="padding: 12px; min-width: 250px; font-family: system-ui, -apple-system, sans-serif;">
              <div style="font-size: 14px; font-weight: 600; color: #111827; margin-bottom: 8px; border-bottom: 2px solid ${color}; padding-bottom: 6px;">
                Location Details
              </div>
              <div style="display: grid; gap: 6px; font-size: 13px;">
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">ICCID:</span>
                  <span style="font-family: monospace; font-weight: 500;">...${iccid.slice(-8)}</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Events:</span>
                  <span style="font-weight: 600; color: #0284c7;">${count.toLocaleString()}</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Data Used:</span>
                  <span style="font-weight: 500;">${formatBytes(bytesUsed)}</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">First Seen:</span>
                  <span style="font-size: 12px;">${new Date(firstSeen).toLocaleString()}</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Last Seen:</span>
                  <span style="font-size: 12px;">${new Date(lastSeen).toLocaleString()}</span>
                </div>
              </div>
            </div>
          `;

          infoWindowRef.current.setContent(content);
          infoWindowRef.current.setPosition({ lat, lng: lon });
          infoWindowRef.current.open(map);
        });

        circlesRef.current.push(circle);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      console.error('Heatmap fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [map, filters]);

  // Fetch on viewport change (debounced)
  useEffect(() => {
    if (!map) return;

    let timeoutId: NodeJS.Timeout;

    const onIdle = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const bounds = map.getBounds();
        if (!bounds) return;

        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        fetchHeatmap({
          northEast: { lat: ne.lat(), lng: ne.lng() },
          southWest: { lat: sw.lat(), lng: sw.lng() },
        });
      }, 500);
    };

    const listener = map.addListener('idle', onIdle);
    onIdle(); // Initial load

    return () => {
      window.google.maps.event.removeListener(listener);
      clearTimeout(timeoutId);
    };
  }, [map, fetchHeatmap]);

  return (
    <div className="relative w-full h-full">
      <div ref={mapRef} className="w-full h-full" />

      {loading && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-10 animate-pulse">
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span className="font-medium">Loading heatmap...</span>
        </div>
      )}

      {error && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-10 max-w-md">
          <span className="text-xl">⚠️</span>
          <span className="font-medium text-sm">{error}</span>
        </div>
      )}

      {metadata && !loading && (
        <div className="absolute bottom-4 left-4 bg-white px-4 py-3 rounded-lg shadow-lg z-10 border border-gray-200">
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-xl">👥</span>
              <span className="text-gray-600">Users:</span>
              <span className="font-bold text-gray-900">{metadata.totalIccids.toLocaleString()}</span>
            </div>
            <div className="w-px h-4 bg-gray-300" />
            <div className="flex items-center gap-2">
              <span className="text-xl">📍</span>
              <span className="text-gray-600">Points:</span>
              <span className="font-bold text-gray-900">{metadata.totalPoints.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HeatmapView;

